/**
 * Gemeinsames Vokabular für das Liefer- bzw. Abholintervall einer Versorgung.
 *
 * Liegt in `libs/shared`, weil Leistungserbringer-Portal und Patientenportal dieselbe Auswahl
 * anbieten. Vorher stand sie viermal wortgleich im Code (drei Versorgungs-Tabs + Patientenportal)
 * und lief in der Wortwahl bereits auseinander: die Seite sagte „Monatlich", der Dialog daneben
 * „Monatliche Lieferung" — man konnte nicht erkennen, dass beides dasselbe meint.
 */

/** Die drei Intervalle, die sich an einer laufenden Versorgung noch umstellen lassen. */
export type ChangeableDeliveryInterval = 'MONTHLY' | 'BIMONTHLY' | 'QUARTERLY'

/** Fachwort der aufrufenden Ansicht: Pflegeboxen werden geliefert, Filialversorgungen abgeholt. */
export type DeliveryIntervalKind = 'Lieferung' | 'Abholung'

export interface DeliveryIntervalOption {
  readonly value: ChangeableDeliveryInterval
  /** Kurzname — steht so auch auf der Karte der Versorgung, damit Seite und Dialog dasselbe Wort benutzen. */
  readonly label: string
  readonly icon: string
  /** Wie viele Monate eine Sendung abdeckt; die Mengen werden entsprechend hochgerechnet. */
  readonly months: number
}

export const DELIVERY_INTERVAL_OPTIONS: readonly DeliveryIntervalOption[] = [
  { value: 'MONTHLY', label: 'Monatlich', icon: 'delivery_truck_speed', months: 1 },
  { value: 'BIMONTHLY', label: 'Zweimonatlich', icon: 'eco', months: 2 },
  { value: 'QUARTERLY', label: 'Quartalsweise', icon: 'eco', months: 3 },
]

export function isChangeableDeliveryInterval(value: string | null | undefined): value is ChangeableDeliveryInterval {
  return DELIVERY_INTERVAL_OPTIONS.some((option) => option.value === value)
}

/**
 * Eine einmalige Versorgung hat keinen Rhythmus — an ihr gibt es nichts umzustellen.
 *
 * Der Umstellen-Knopf entfaellt deshalb (PG08-Einlagen, Selbstzahler-Einmalkauf, PG 51): er haette
 * aus der einmaligen Versorgung stumm ein laufendes Abo gemacht, und der Dialog kennt den Weg
 * zurueck nicht — er bietet nur MONTHLY/BIMONTHLY/QUARTERLY an. Wer wirklich umstellen will, nimmt
 * den HiMi-Stepper; das Backend lehnt den schnellen Wechsel seit 09/2026 mit 409 ab
 * (`DELIVERY_INTERVAL_CHANGE_BLOCKED_BY_ONE_TIME_ORDER`).
 *
 * Ein unbekanntes/fehlendes Intervall gilt bewusst NICHT als einmalig — dort bleibt der Knopf, wie
 * er war.
 */
export function isOneTimeDeliveryInterval(value: string | null | undefined): boolean {
  return value === 'ONE_TIME_ORDER'
}

export function deliveryIntervalOption(value: string | null | undefined): DeliveryIntervalOption | undefined {
  return DELIVERY_INTERVAL_OPTIONS.find((option) => option.value === value)
}

/** Name des Feldes — im Abholkontext heisst dasselbe Feld „Abholrhythmus" (so nennt es dort auch der Info-Text). */
export function deliveryIntervalTerm(kind: DeliveryIntervalKind): string {
  return kind === 'Abholung' ? 'Abholrhythmus' : 'Lieferintervall'
}

/** Feldname mit Artikel für ganze Sätze (Snackbars, Tooltips) — das Genus unterscheidet sich je Kontext. */
export function deliveryIntervalTermArticled(kind: DeliveryIntervalKind): string {
  return kind === 'Abholung' ? 'Der Abholrhythmus' : 'Das Lieferintervall'
}

/** Was das Intervall konkret bedeutet — beantwortet „und was heisst das jetzt?", ohne dass jemand nachrechnet. */
export function deliveryIntervalDescription(option: DeliveryIntervalOption, kind: DeliveryIntervalKind): string {
  if (option.months === 1) {
    return `Jeden Monat eine ${kind}.`
  }
  if (kind === 'Abholung') {
    return `Alle ${option.months} Monate eine Abholung, mit der Menge für ${option.months} Monate.`
  }
  return `Alle ${option.months} Monate eine Lieferung, mit der Menge für ${option.months} Monate. Weniger Versand — die nachhaltigere Variante.`
}
